import streamSaver from "streamsaver";
import { getRetouchAdminToken, getRetouchReviewApiBase } from "./retouchReviewApi";

let streamSaverConfigured = false;
const MAX_DOWNLOAD_ATTEMPTS = 3;
const RETRY_DELAY_MS = 500;

function publicUrlPrefix() {
    const value = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
    return value === "." ? "" : value;
}

function configureStreamSaver() {
    if (streamSaverConfigured) return;
    streamSaver.mitm = `${window.location.origin}${publicUrlPrefix()}/mitm.html`;
    streamSaverConfigured = true;
}

function canStreamToFile() {
    return typeof WritableStream !== "undefined" && typeof window !== "undefined" && window.isSecureContext !== false;
}

function fallbackDownload(blob, filename) {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "download";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

function filenameFromResponse(response, fallback) {
    const disposition = response.headers.get("Content-Disposition") || "";
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (encoded) {
        try {
            return decodeURIComponent(encoded);
        } catch {
            return encoded;
        }
    }
    return String(fallback || "download").split("/").pop() || "download";
}

function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function streamResponse(response, filename) {
    configureStreamSaver();
    const size = Number(response.headers.get("Content-Length"));
    const writable = streamSaver.createWriteStream(filename || "download", {
        size: Number.isFinite(size) && size > 0 ? size : undefined,
    });
    if (response.body?.pipeTo) {
        await response.body.pipeTo(writable);
        return;
    }
    const writer = writable.getWriter();
    try {
        if (!response.body) {
            await writer.write(new Uint8Array(await response.arrayBuffer()));
        } else {
            const reader = response.body.getReader();
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    await writer.write(value);
                }
            } finally {
                reader.releaseLock();
            }
        }
        await writer.close();
    } catch (error) {
        await writer.abort(error).catch(() => {});
        throw error;
    }
}

async function fetchDownload(url, headers, signal) {
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_DOWNLOAD_ATTEMPTS; attempt += 1) {
        try {
            const response = await fetch(url, { headers, cache: "no-store", signal });
            if (response.ok || response.status < 500 || attempt === MAX_DOWNLOAD_ATTEMPTS) return response;
            await response.body?.cancel?.();
        } catch (error) {
            if (signal?.aborted) throw error;
            lastError = error;
        }
        await wait(RETRY_DELAY_MS * attempt);
    }
    throw lastError || new Error("Download failed");
}

export async function downloadRetouchReviewPhoto({ token = "", photoId, version, relativePath, signal }) {
    const base = getRetouchReviewApiBase();
    const params = new URLSearchParams({ photoId, version });
    const path = token
        ? `/api/retouch-review/download/${encodeURIComponent(token)}?${params.toString()}`
        : `/api/retouch-review/download?${params.toString()}`;
    const headers = token ? { "X-Retouch-Token": token } : { Authorization: `Bearer ${getRetouchAdminToken()}` };
    const response = await fetchDownload(`${base}${path}`, headers, signal);
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error?.message || `Download failed (${response.status})`);
    }
    const filename = filenameFromResponse(response, relativePath);
    if (canStreamToFile()) await streamResponse(response, filename);
    else fallbackDownload(await response.blob(), filename);
}
